<template>
  <div
    :class="[
      'app-date-input',
      $attrs.class,
      { 'app-date-input--hebrew': !isGregorianDisplay, 'app-date-input--disabled': disabled },
    ]"
  >
    <input
      v-if="isGregorianDisplay"
      v-bind="controlAttrs"
      type="date"
      class="app-date-input__native"
      :value="dateValue"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      @input="onNativeInput"
      @change="onNativeChange"
      @blur="$emit('blur', $event)"
    />
    <button
      v-else
      v-bind="controlAttrs"
      type="button"
      class="app-date-input__hebrew-trigger"
      :class="{ 'is-placeholder': !dateValue, 'is-open': showHebrewPicker }"
      :disabled="disabled || readonly"
      :aria-expanded="String(showHebrewPicker)"
      aria-haspopup="dialog"
      @click="showHebrewPicker = true"
      @blur="$emit('blur', $event)"
    >
      <span dir="rtl">{{ hebrewDateLabel }}</span>
      <i class="pi pi-calendar" aria-hidden="true"></i>
    </button>

    <Teleport to="body">
      <JewishDatePicker
        v-if="!isGregorianDisplay && showHebrewPicker"
        :initial-date="dateValue"
        @select-date="onHebrewDateSelected"
        @close-date-picker-modal="showHebrewPicker = false"
      />
    </Teleport>
  </div>
</template>

<script>
import { HDate } from 'hebcal';
import JewishDatePicker from '@/components/Jewish_Date_Picker.vue';
import { DAYS_IN_HEBREW, HEBREW_MONTHS, MONTHS_IN_HEBREW } from '@/utils/const';
import { formatHebrewYear } from '@/utils/hebrew_year';
import {
  toHebrewPickerDateInputValue,
  toWallClockDate,
  toWallClockDateInputValue,
} from '@/utils/wall_clock_date';

export default {
  name: 'AppDateInput',
  components: { JewishDatePicker },
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Date],
      default: undefined,
    },
    value: {
      type: [String, Date],
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'input', 'change', 'blur'],
  data() {
    return {
      showHebrewPicker: false,
    };
  },
  computed: {
    sourceValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    // This is the component's contract with every backend caller: no matter
    // which calendar the user sees, the form model receives YYYY-MM-DD.
    dateValue() {
      return toWallClockDateInputValue(this.sourceValue);
    },
    controlAttrs() {
      const {
        class: ignoredClass,
        onInput: ignoredInput,
        onChange: ignoredChange,
        onBlur: ignoredBlur,
        ...attrs
      } = this.$attrs;
      return attrs;
    },
    hebrewDateLabel() {
      const gregorian = toWallClockDate(this.dateValue);
      if (!gregorian) return this.$attrs.placeholder || 'Select Hebrew date';
      const hebrew = new HDate(gregorian);
      const monthIndex = HEBREW_MONTHS.indexOf(hebrew.getMonthName());
      const month = monthIndex >= 0
        ? MONTHS_IN_HEBREW[monthIndex]
        : hebrew.getMonthName();
      return `${DAYS_IN_HEBREW[hebrew.getDate() - 1] || hebrew.getDate()} ${month} ${formatHebrewYear(hebrew.getFullYear())}`;
    },
  },
  watch: {
    isGregorianDisplay(isGregorian) {
      if (isGregorian) this.showHebrewPicker = false;
    },
  },
  methods: {
    emitValue(value, originalEvent = null) {
      const normalized = toWallClockDateInputValue(value);
      this.$emit('update:modelValue', normalized);
      const event = originalEvent || {
        type: 'change',
        target: { value: normalized },
        currentTarget: { value: normalized },
      };
      this.$emit('input', event);
      this.$emit('change', event);
    },
    onNativeInput(event) {
      const normalized = toWallClockDateInputValue(event.target.value);
      this.$emit('update:modelValue', normalized);
      this.$emit('input', event);
    },
    onNativeChange(event) {
      const normalized = toWallClockDateInputValue(event.target.value);
      this.$emit('update:modelValue', normalized);
      this.$emit('change', event);
    },
    onHebrewDateSelected(date) {
      const value = toHebrewPickerDateInputValue(date);
      this.showHebrewPicker = false;
      this.emitValue(value);
    },
  },
};
</script>

<style scoped>
.app-date-input {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  padding: 0 !important;
}

.app-date-input__native,
.app-date-input__hebrew-trigger {
  width: 100%;
  height: 100%;
  min-height: inherit;
  padding: 0 12px;
  border: 0 !important;
  border-radius: inherit;
  outline: 0 !important;
  background: transparent;
  color: inherit;
  font: inherit;
  box-shadow: none !important;
}

.app-date-input__native {
  color-scheme: light;
}

.app-date-input__hebrew-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  text-align: left;
}

.app-date-input__hebrew-trigger > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-date-input__hebrew-trigger > .pi {
  flex: 0 0 auto;
  color: var(--color-primary-hover);
  font-size: 0.78rem;
}

.app-date-input__hebrew-trigger.is-placeholder {
  color: var(--color-text-muted);
}

.app-date-input__hebrew-trigger.is-open {
  background: var(--color-primary-soft);
}

.app-date-input--disabled {
  opacity: 0.65;
}
</style>
